import { createDomain, sample, combine } from 'effector'
import { createGate, useStoreMap } from 'effector-react'
import { mainBalance } from '~/entities/MainBalance'
import { AxiosErrorType, Debt } from '~/shared/api'
import { createCache } from '~/shared/lib/mapCacheFactory'
import { mapMessageErrors } from '~/shared/lib/mapMessageErrors'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { isString } from '~/shared/lib/utils'

export const Gate = createGate<{ debtId: UniqueId }>()

export const domain = createDomain('features.debts.view')

export const requestFx = domain.createEffect<UniqueId, Debt>({
  handler: fetchDebt,
})

const {
  $cache: $debtsCache,
  useCache: useDebtCache,
  updateCache,
} = createCache<Debt>({
  domain,
  getEntityId: (debt) => debt.getApiId() as UniqueId,
})
export { $debtsCache, useDebtCache }

$debtsCache.on(requestFx.doneData, (cache, debt) => updateCache(cache, [debt]))

export const $debtsError = domain
  .createStore<Record<UniqueId, Error>>({})
  .on(requestFx.fail, (store, { error, params: id }) => ({
    [id]: error,
    ...store,
  }))
  .on(requestFx.done, (store, { params: id }) => {
    const errors = { ...store }
    delete errors[id]
    return errors
  })

export const useDebtError = (id: UniqueId) =>
  useStoreMap($debtsError, (errors) => errors[id])

async function fetchDebt(id: UniqueId) {
  const response = await Debt.with('car').find(id)
  return response.getData() as Debt
}

export const $debtId = domain
  .createStore<UniqueId | null>(null)
  .on(Gate.state, (_, { debtId }) => debtId)

export const $debt = combine($debtId, $debtsCache, (id, cache) => {
  if (!id) return null
  return cache.map[id] ?? null
})

sample({
  clock: Gate.state,
  filter: ({ debtId }) => isString(debtId),
  fn: ({ debtId }) => debtId,
  target: requestFx,
})

// --- Repay drawer ---

export const openRepayDrawer = domain.createEvent()
export const closeRepayDrawer = domain.createEvent()

export const $isRepayDrawerOpen = domain
  .createStore(false)
  .on(openRepayDrawer, () => true)
  .on(closeRepayDrawer, () => false)

type RepayParams = { debtId: UniqueId; amount: number }

export const repayFx = domain.createEffect<RepayParams, void, AxiosErrorType>({
  handler: ({ debtId, amount }) => Debt.repay(debtId, amount),
})

export const repay = domain.createEvent<number>()

sample({
  clock: repay,
  source: $debtId,
  filter: isString,
  fn: (debtId, amount): RepayParams => ({ debtId: debtId as UniqueId, amount }),
  target: repayFx,
})

sample({
  clock: openRepayDrawer,
  target: mainBalance.getCurrentBalanceSilentFx,
})

sample({
  clock: repayFx.done,
  source: $debtId,
  filter: isString,
  fn: (debtId) => debtId as UniqueId,
  target: requestFx,
})

sample({
  clock: repayFx.done,
  target: closeRepayDrawer,
})

sample({
  clock: repayFx.done,
  fn: () => ({
    message: 'Платёж успешно проведён',
    variant: 'success' as const,
  }),
  target: snackbarEnqueued,
})

sample({
  clock: repayFx.failData,
  fn: (e) => ({ message: mapMessageErrors(e), variant: 'danger' as const }),
  target: snackbarEnqueued,
})
